import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FullPageStatus, Input, showToast } from "@vv/ui";
import { DndContext, type DragEndEvent } from "@dnd-kit/core";
import type { RequestStatus } from "@vv/requests-frontend";
import {
  acceptRequest,
  approveReworkRequest,
  AssignModal,
  CompleteModal,
  completeRequest,
  fetchRequests,
  RejectModal,
  rejectRequest,
  rejectReworkRequest,
  RequestReworkModal,
  requestReworkAction,
  startRequest,
  useRequestsDispatch,
  useRequestsSelector,
  type AssignPayload,
  type CompletePayload,
  type ValidationRequest,
  type WorkflowAction,
} from "@vv/requests-frontend";
import type { TeamRole } from "@vv/teams-frontend";
import {
  fetchTeamMembers,
  fetchTeams,
  useTeamsDispatch,
  useTeamsSelector,
} from "@vv/teams-frontend";
import { useEffect, useMemo, useRef, useState } from "react";
import { KanbanCard } from "../components/KanbanCard";
import { KanbanColumn } from "../components/KanbanColumn";
import { canDragTo } from "../domain/kanban-permissions";
import {
  COLUMN_LABELS,
  COLUMN_ORDER,
  MODAL_ACTIONS,
  TRANSITION_ACTIONS,
} from "../domain/kanban.constants";

export interface KanbanPageProps {
  currentUserId: string;
  isOwner: boolean;
}

const CARD_RENDER_LIMIT = 30;
const TEAM_MEMBERS_FETCH_LIMIT = 20;

interface ModalState {
  action: "accept" | "reject" | "complete" | "requestRework" | "approveRework";
  request: ValidationRequest;
}

export function KanbanPage({ currentUserId, isOwner }: KanbanPageProps) {
  const requestsDispatch = useRequestsDispatch();
  const teamsDispatch = useTeamsDispatch();

  const requests = useRequestsSelector((state) => state.requests.requests);
  const requestsStatus = useRequestsSelector((state) => state.requests.status);
  const teams = useTeamsSelector((state) => state.teams.teams);
  const members = useTeamsSelector((state) => state.teams.members);

  const [teamFilter, setTeamFilter] = useState("");
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const requestedTeamIdsRef = useRef(new Set<string>());

  useEffect(() => {
    requestsDispatch(fetchRequests());
    teamsDispatch(fetchTeams());
  }, [requestsDispatch, teamsDispatch]);

  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const team of teams) map.set(team.id, team.name);
    return map;
  }, [teams]);

  const visibleRequests = teamFilter
    ? requests.filter((request) => request.teamId === teamFilter)
    : requests;

  const columns = useMemo(() => {
    const grouped = new Map<RequestStatus, ValidationRequest[]>();
    for (const status of COLUMN_ORDER) grouped.set(status, []);
    for (const request of visibleRequests) {
      grouped.get(request.status)?.push(request);
    }
    return grouped;
  }, [visibleRequests]);

  // What's actually rendered (post-cap, see CARD_RENDER_LIMIT below) - used
  // to bound the team-member fetch effect below it. Deriving this from the
  // full `requests` list instead would fire one `fetchTeamMembers` call per
  // distinct team *ever referenced by any request in the system*, which
  // scales with total history, not with what's on screen.
  const renderedRequests = useMemo(
    () => COLUMN_ORDER.flatMap((status) => (columns.get(status) ?? []).slice(0, CARD_RENDER_LIMIT)),
    [columns],
  );

  useEffect(() => {
    // Capped, not just deduped: a long-lived board can still reference dozens
    // of distinct teams among its rendered cards, and firing that many
    // fetches at once - each completion re-triggering this effect as
    // `members` gets a new reference - keeps the page re-rendering long
    // enough to make it feel unresponsive. Teams beyond the cap simply can't
    // be dragged (canDragTo treats an unknown team's roles as empty) until
    // the team filter narrows things down.
    //
    // `requestedTeamIdsRef` tracks every teamId we've ever *attempted* to
    // fetch this mount, independent of `members` (which only reflects
    // *successful* loads). Without it, a single failed request (e.g. a rate
    // limit or an auth-token race on initial load) never gets marked as
    // done, so every subsequent re-render redispatches it - turning one
    // transient failure into a self-sustaining retry storm.
    const uniqueTeamIds = [...new Set(renderedRequests.map((request) => request.teamId))].slice(
      0,
      TEAM_MEMBERS_FETCH_LIMIT,
    );
    for (const teamId of uniqueTeamIds) {
      if (!members[teamId] && !requestedTeamIdsRef.current.has(teamId)) {
        requestedTeamIdsRef.current.add(teamId);
        teamsDispatch(fetchTeamMembers(teamId));
      }
    }
  }, [renderedRequests, members, teamsDispatch]);

  function myRolesFor(request: ValidationRequest): TeamRole[] {
    const teamMembers = members[request.teamId] ?? [];
    return teamMembers
      .filter((member) => member.userId === currentUserId)
      .map((member) => member.role);
  }

  async function runAction(action: WorkflowAction, request: ValidationRequest, payload?: unknown) {
    try {
      switch (action) {
        case "accept":
          await requestsDispatch(
            acceptRequest({ id: request.id, payload: payload as AssignPayload }),
          ).unwrap();
          break;
        case "start":
          await requestsDispatch(startRequest(request.id)).unwrap();
          break;
        case "approveRework":
          await requestsDispatch(
            approveReworkRequest({ id: request.id, payload: payload as AssignPayload }),
          ).unwrap();
          break;
        case "rejectRework":
          await requestsDispatch(rejectReworkRequest(request.id)).unwrap();
          break;
        case "reject":
          await requestsDispatch(
            rejectRequest({ id: request.id, payload: { rejectionReason: payload as string } }),
          ).unwrap();
          break;
        case "requestRework":
          await requestsDispatch(
            requestReworkAction({ id: request.id, payload: { reworkReason: payload as string } }),
          ).unwrap();
          break;
        case "complete":
          await requestsDispatch(
            completeRequest({ id: request.id, payload: payload as CompletePayload }),
          ).unwrap();
          break;
        default:
          break;
      }
      showToast(`${request.displayId} updated`, "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not update the request", "error");
      throw err;
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;

    const request = requests.find((candidate) => candidate.id === active.id);
    if (!request) return;

    const targetStatus = over.id as RequestStatus;
    if (targetStatus === request.status) return;

    const action = TRANSITION_ACTIONS[request.status]?.[targetStatus];
    if (!action) {
      showToast(
        `Cannot move a request directly from ${request.status} to ${targetStatus}`,
        "error",
      );
      return;
    }

    const myRoles = myRolesFor(request);
    if (!canDragTo(action, request, isOwner, myRoles, currentUserId)) {
      showToast("You don't have permission to do that", "error");
      return;
    }

    if (MODAL_ACTIONS.has(action)) {
      setModalState({ action: action as ModalState["action"], request });
      return;
    }

    void runAction(action, request);
  }

  if (requestsStatus === "loading" && requests.length === 0) {
    return <FullPageStatus variant="loading" title="Loading requests..." />;
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Kanban
        </Typography>
        <Input
          select
          label="Team"
          value={teamFilter}
          onChange={(event) => setTeamFilter(event.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All teams</MenuItem>
          {teams.map((team) => (
            <MenuItem key={team.id} value={team.id}>
              {team.name}
            </MenuItem>
          ))}
        </Input>
      </Stack>

      <DndContext onDragEnd={handleDragEnd}>
        <Stack direction="row" spacing={2} sx={{ overflowX: "auto", pb: 2 }}>
          {COLUMN_ORDER.map((status) => {
            const columnRequests = columns.get(status) ?? [];
            // Every card mounts a live dnd-kit draggable (pointer listeners,
            // layout measurement) - rendering hundreds of them in one column
            // makes the page sluggish long before pagination lands as its
            // own feature. Capping here keeps the board usable in the
            // meantime; the team filter is the way to see the rest.
            const visibleCards = columnRequests.slice(0, CARD_RENDER_LIMIT);
            const hiddenCount = columnRequests.length - visibleCards.length;
            return (
              <KanbanColumn
                key={status}
                status={status}
                label={COLUMN_LABELS[status]}
                count={columnRequests.length}
              >
                {visibleCards.map((request) => {
                  const myRoles = myRolesFor(request);
                  const possibleActions = Object.values(TRANSITION_ACTIONS[request.status] ?? {});
                  const canMove = possibleActions.some((action) =>
                    canDragTo(action, request, isOwner, myRoles, currentUserId),
                  );
                  return (
                    <KanbanCard
                      key={request.id}
                      request={request}
                      teamName={teamNameById.get(request.teamId) ?? "—"}
                      disabled={!canMove}
                    />
                  );
                })}
                {hiddenCount > 0 ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 1 }}
                  >
                    +{hiddenCount} more - use the team filter to narrow this down
                  </Typography>
                ) : null}
              </KanbanColumn>
            );
          })}
        </Stack>
      </DndContext>

      {modalState?.action === "reject" ? (
        <RejectModal
          open
          onClose={() => setModalState(null)}
          onConfirm={async (reason) => {
            await runAction("reject", modalState.request, reason);
            setModalState(null);
          }}
        />
      ) : null}
      {modalState?.action === "requestRework" ? (
        <RequestReworkModal
          open
          onClose={() => setModalState(null)}
          onConfirm={async (reason) => {
            await runAction("requestRework", modalState.request, reason);
            setModalState(null);
          }}
        />
      ) : null}
      {modalState?.action === "accept" || modalState?.action === "approveRework" ? (
        <AssignModal
          open
          onClose={() => setModalState(null)}
          title={
            modalState.action === "approveRework" ? "Approve rework request" : "Accept request"
          }
          confirmLabel={modalState.action === "approveRework" ? "Approve" : "Accept"}
          onConfirm={async (payload) => {
            await runAction(modalState.action, modalState.request, payload);
            setModalState(null);
          }}
        />
      ) : null}
      {modalState?.action === "complete" ? (
        <CompleteModal
          open
          onClose={() => setModalState(null)}
          expectedCompletionDate={modalState.request.expectedCompletionDate}
          onConfirm={async (payload) => {
            await runAction("complete", modalState.request, payload);
            setModalState(null);
          }}
        />
      ) : null}
    </Box>
  );
}
